import { useEffect, useState } from 'react'
import { Box, TablePagination } from '@mui/material'
import { fetchWithAuth } from '../utils/fetchWithAuth'
import { createCsvBlob, repairCsvText } from '../utils/csvEncoding'

const AdminAuditLogs = () => {
  const [logs, setLogs] = useState([])
  const [filters, setFilters] = useState({
    id: '',
    userId: '',
    action: '',
    details: '',
    timestamp: '',
    createdAt: ''
  })
  const [sort, setSort] = useState({ field: 'createdAt', dir: 'desc' })
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)

  const fetchData = async () => {
    const res = await fetchWithAuth('/admin/audit-logs')
    const data = await res.json()
    if (Array.isArray(data)) {
      setLogs(data)
    } else {
      setLogs([])
    }
  }

  useEffect(() => { fetchData() }, [])

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }))
    setPage(0)
  }

  const actionOptions = Array.from(new Set(logs.map(l => l.action).filter(Boolean)))

  const filtered = logs.filter(l =>
    (!filters.id || String(l.id).includes(filters.id)) &&
    (!filters.userId || String(l.userId).includes(filters.userId)) &&
    (!filters.action || l.action === filters.action) &&
    (!filters.details || (l.details || '').toLowerCase().includes(filters.details.toLowerCase())) &&
    (!filters.timestamp || (l.timestamp && new Date(l.timestamp) >= new Date(filters.timestamp))) &&
    (!filters.createdAt || (l.createdAt && new Date(l.createdAt) >= new Date(filters.createdAt)))
  )

  const sorted = [...filtered].sort((a, b) => {
    const { field, dir } = sort
    const av = a[field]
    const bv = b[field]
    if (av == null && bv == null) return 0
    if (av == null) return dir === 'asc' ? -1 : 1
    if (bv == null) return dir === 'asc' ? 1 : -1
    if (av < bv) return dir === 'asc' ? -1 : 1
    if (av > bv) return dir === 'asc' ? 1 : -1
    return 0
  })

  const paginated = sorted.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  )

  const onSort = (field) => {
    setSort(prev =>
      prev.field === field
        ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { field, dir: 'asc' }
    )
    setPage(0)
  }

  const renderSort = (field, label) => (
    <th
      style={{
        cursor: 'pointer',
        position: 'sticky',
        top: 0,
        zIndex: 2,
        background: '#c8e1e1'
      }}
      onClick={() => onSort(field)}
    >
      {label} {sort.field === field ? (sort.dir === 'asc' ? '▲' : '▼') : ''}
    </th>
  )

  // --- EXPORT FUNKCIE ---
  const exportCSV = () => {
    if (!sorted.length) return
    const header = Object.keys(sorted[0])
    const rows = sorted.map(r => header.map(h => `"${repairCsvText(r[h]).replace(/"/g, '""')}"`).join(','))
    const csv = [header.join(','), ...rows].join('\n')
    const blob = createCsvBlob(csv)
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'audit_logs.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  const exportJSON = () => {
    const blob = new Blob([JSON.stringify(sorted, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'audit_logs.json'
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div
      style={{
        background: '#ededed',
        padding: 12,
        border: '1.5px solid #107c6b',
        borderRadius: 10,
        width: '100%',
        boxSizing: 'border-box',
        position: 'relative',
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <h2>Audit Logs</h2>

      {/* 🔍 FILTRE NAD TABUĽKOU */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        <input placeholder="ID" value={filters.id} onChange={e => handleFilterChange('id', e.target.value)} />
        <input placeholder="User ID" value={filters.userId} onChange={e => handleFilterChange('userId', e.target.value)} />
        <select value={filters.action} onChange={e => handleFilterChange('action', e.target.value)}>
          <option value="">-- Action --</option>
          {actionOptions.map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
        <input placeholder="Details" value={filters.details} onChange={e => handleFilterChange('details', e.target.value)} />
        <label>
          Timestamp:
          <input type="datetime-local" value={filters.timestamp} onChange={e => handleFilterChange('timestamp', e.target.value)} />
        </label>
        <label>
          Created At:
          <input type="datetime-local" value={filters.createdAt} onChange={e => handleFilterChange('createdAt', e.target.value)} />
        </label>
      </div>

      {/* 📤 EXPORT BUTTONS */}
      <div style={{ marginBottom: 15 }}>
        <button onClick={exportCSV} style={{ marginRight: 10 }}>Export CSV</button>
        <button onClick={exportJSON}>Export JSON</button>
      </div>

      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          border: '1px solid #c7c7c7',
          borderRadius: 10,
          background: '#fff'
        }}
      >
        <table border="1" cellPadding="4">
          <thead>
            <tr>
              {renderSort('id', 'ID')}
              {renderSort('userId', 'User ID')}
              {renderSort('action', 'Action')}
              {renderSort('details', 'Details')}
              {renderSort('timestamp', 'Timestamp')}
              {renderSort('createdAt', 'Created At')}
            </tr>
          </thead>
          <tbody>
            {paginated.map(log => (
              <tr key={log.id}>
                <td>{log.id}</td>
                <td>{log.userId}</td>
                <td>{log.action}</td>
                <td>{log.details}</td>
                <td>{log.timestamp}</td>
                <td>{log.createdAt}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <TablePagination
          component="div"
          count={sorted.length}
          page={page}
          onPageChange={(_e, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => {
            setRowsPerPage(parseInt(e.target.value, 10))
            setPage(0)
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Na stránku"
        />
      </Box>
    </div>
  )
}

export default AdminAuditLogs